// Contrato de la pantalla Políticas (Admin SureKeep): tipos + constantes de UI.
// El mapeo wire ↔ modelo de las políticas especiales vive en `api/storePolicyMappers.ts`.
import { SaleTypeEnum } from '@fiado/type-kit/bin/loanOfferings/enums/SaleTypeEnum';
import type { SegmentColorSlot } from '@/shared/patterns/Pills';
import type {
    CreditPlanLevelWire,
    PrepaymentMinimumModeWire,
    StorePolicyLogActionWire,
    StorePolicyPhaseWire,
    StorePolicyStatusWire,
} from '@/api/services/loan-offerings/contract';

export type PlanTier = 'bronce' | 'plata' | 'oro' | 'empleado';
/** Niveles del segmento Estándar (el plan Empleado vive como política del segmento Empleados). */
export type StandardTier = 'bronce' | 'plata' | 'oro';

export type PrepaymentMinimumMode = PrepaymentMinimumModeWire;

/**
 * Mínimo del abono a capital, en las unidades de la pantalla: el porcentaje como fracción (0.5 =
 * 50 %) y el monto en pesos. Solo lleva el dato de su modo; `null` es que el operador no lo capturó.
 */
export interface PlanPrepaymentMinimum {
    mode: PrepaymentMinimumMode;
    periodPaymentPct?: number | null;
    amount?: number | null;
}

export interface CreditPlan {
    /** Id del plan en loan-offerings — clave para PUT /credit-plans/{planId}. '' en el catálogo mock. */
    planId: string;
    tier: PlanTier;
    sciMin: number;
    sciMax: number;
    /** TNA como fracción anual (2.60 = 260%). */
    tna: number;
    /** IVA sobre intereses como fracción (0.16 = 16%). Se muestra; ningún endpoint lo edita. */
    ivaOnInterest: number;
    /** Enganche mín/máx como fracción (0.40 = 40%). */
    downMinPct: number;
    downMaxPct: number;
    maxAmount: number;
    termsWeeks: number[];
    /** Comisión de apertura como fracción (0 = deshabilitada). */
    openingFeePct: number;
    active: boolean;
    lastModified: string;
    version: number;
    /** Tipos de venta a los que aplica el plan. Sin `NEW` el wizard de venta nueva no lo ofrece. */
    saleTypes: SaleTypeEnum[];
    /** Mínimo del abono a capital. Un plan que no lo trae es «Cualquier monto». */
    prepaymentMinimum: PlanPrepaymentMinimum;
}

export type PolicyType =
    'suspend' | 'sci_min' | 'down_min' | 'tna' | 'max_amount' | 'allowed_skus' | 'combo';

export type StorePolicyStatus = StorePolicyStatusWire;
export type StorePolicyPhase = StorePolicyPhaseWire;
export type StorePolicyLogAction = StorePolicyLogActionWire;
export type PolicyLevel = CreditPlanLevelWire;

/**
 * El estado que LEE el operador: junta el `status` persistido con la `phase` que el backend deriva
 * de las fechas. Es lo que pinta el badge y lo que filtra la lista.
 */
export type PolicyStatus = 'DRAFT' | 'SCHEDULED' | 'ACTIVE' | 'PAUSED' | 'EXPIRED' | 'ARCHIVED';

/** Las cuatro acciones de estado de la pantalla, cada una un `PUT /store-policies/{id}/status`. */
export type PolicyAction = 'publish' | 'pause' | 'resume' | 'expire' | 'archive';

export type PolicyLeverKey = 'sciMin' | 'downMinPct' | 'tna' | 'maxAmount';

/**
 * ENDURECER sube el piso (o baja el techo) del plan; AFLOJAR hace lo contrario y solo aplica si
 * mejora. El backend rechaza la misma palanca en los dos grupos, así que cada una lleva una sola.
 */
export type PolicyLeverDirection = 'harden' | 'ease';

/** Una palanca prendida: cuánto y hacia dónde. */
export interface PolicyLever {
    /** Unidades visibles: puntos de SCI, puntos de %, y pesos en el monto. */
    value: number;
    direction: PolicyLeverDirection;
}

/** Las palancas prendidas de una política. Una clave ausente = palanca apagada. */
export type PolicyLevers = Partial<Record<PolicyLeverKey, PolicyLever>>;

/** Una política especial tal como la muestra la pantalla. */
export interface SpecialPolicy {
    /** `policyId` del backend — un ULID. Cadena vacía mientras el borrador no se creó. */
    id: string;
    name: string;
    description: string;
    /** COMPUERTA que niega la venta en el alcance. Al prenderla se ignoran las palancas. */
    suspend: boolean;
    levers: PolicyLevers;
    /** COMPUERTA de SKU: solo estos productos se financian. Vacío = sin restricción. */
    allowedSkus: string[];
    /** ALCANCE. Vacío = todos los niveles. */
    targetLevels: PolicyLevel[];
    /** ALCANCE. Vacío = toda la red. */
    storeIds: string[];
    /** ALCANCE (`targetSkus` del wire). Vacío = todos los productos. */
    productSkus: string[];
    /** ALCANCE. Vacío = todos los segmentos. */
    segmentIds: string[];
    validFrom: string | null;
    validTo: string | null;
    status: StorePolicyStatus;
    /** La deriva el backend de las fechas; `null` cuando el estado no es PUBLISHED. */
    phase: StorePolicyPhase | null;
    version: number;
    /** Último motivo asentado. VERBATIM del backend. */
    reason: string | null;
    /** VERBATIM del backend: hoy `system` o el `cognitoSub` crudo, no un nombre. */
    createdBy: string;
}

/** Lo editable de una política: es lo que compara el dirty-state y lo que viaja en el POST/PUT. */
export interface PolicyDraft {
    name: string;
    description: string;
    suspend: boolean;
    levers: PolicyLevers;
    allowedSkus: string[];
    targetLevels: PolicyLevel[];
    storeIds: string[];
    productSkus: string[];
    segmentIds: string[];
    validFrom: string | null;
    validTo: string | null;
}

/** Un renglón del historial de UNA política (`GET /store-policies/{id}/log`). */
export interface StorePolicyLogEntry {
    policyId: string;
    policyName: string | null;
    action: StorePolicyLogAction;
    fieldChanged: string | null;
    oldValue: unknown;
    newValue: unknown;
    occurredAt: string;
    /** VERBATIM del backend: hoy `system` o el `cognitoSub` crudo. */
    performedBy: string;
    /** Nombre legible del actor; `null` mientras nadie lo resuelve — ahí se pinta `performedBy`. */
    performedByName: string | null;
    reason: string | null;
    versionFrom: number | null;
    versionTo: number | null;
}

/** La paleta vive con la píldora que la pinta (`shared/patterns/Pills`); aquí solo se reexporta. */
export type { SegmentColorSlot } from '@/shared/patterns/Pills';

export interface Segment {
    id: string;
    name: string;
    colorSlot: SegmentColorSlot;
    /** El catch-all del tenant. No se ofrece en el alcance: acotar a él no alcanza a nadie. */
    isDefault: boolean;
    /** Del último recálculo; `null` = sin calcular. */
    clientCount: number | null;
    pctTotal: number | null;
    /** La composición cambió y el conteo espera el próximo recálculo. */
    countPending: boolean;
}

/**
 * El chip que NO acota. En Políticas «todos» es la lista VACÍA — el por omisión nunca sale del
 * resolve, así que una política acotada a él no llega a ningún cliente.
 */
export const ALL_SEGMENTS_LABEL = 'Todos los segmentos';

/** Lo que se le dice al operador cuando una política guardada quedó acotada al por omisión. */
export const DEFAULT_SEGMENT_SCOPE_DETAIL = 'Este alcance no llega a ningún cliente';

export type PolicyLogKind = 'plan' | 'store_policy' | 'override';

export interface PolicyLogEntry {
    date: string;
    time: string;
    author: string;
    kind: PolicyLogKind;
    /** Nivel (planes), id de tienda (store_policy) o SKU (override). */
    target: string;
    change: string;
    reason: string;
    versionFrom: number | null;
    versionTo: number | null;
}

// ---------- Constantes de UI (labels es-MX + colores del mock) ----------

export const TIER_LABEL: Record<PlanTier, string> = {
    bronce: 'Bronce',
    plata: 'Plata',
    oro: 'Oro',
    empleado: 'Empleado',
};

export const PREPAYMENT_MINIMUM_MODE_LABEL: Record<PrepaymentMinimumMode, string> = {
    ANY: 'Cualquier monto',
    PERCENT_OF_PERIOD_PAYMENT: '% del pago semanal',
    FIXED: 'Monto fijo',
};

export const SALE_TYPE_LABEL: Record<SaleTypeEnum, string> = {
    [SaleTypeEnum.NEW]: 'Venta nueva',
    [SaleTypeEnum.REPURCHASE]: 'Recompra',
    [SaleTypeEnum.EMPLOYEE]: 'Empleado',
};

export const POLICY_TYPE_LABEL: Record<PolicyType, string> = {
    suspend: 'Suspender',
    sci_min: 'SCI mín',
    down_min: 'Enganche mín',
    tna: 'TNA especial',
    max_amount: 'Monto máx',
    allowed_skus: 'SKU permitidos',
    combo: 'Combo',
};

export const POLICY_STATUS_LABEL: Record<PolicyStatus, string> = {
    DRAFT: 'Borrador',
    SCHEDULED: 'Programada',
    ACTIVE: 'Activa',
    PAUSED: 'Pausada',
    EXPIRED: 'Vencida',
    ARCHIVED: 'Eliminada',
};

/** `.badge-*` del mock por estado. */
export const POLICY_STATUS_BADGE_CLASS: Record<PolicyStatus, string> = {
    DRAFT: 'bg-muted text-muted-foreground',
    SCHEDULED: 'bg-info/10 text-info',
    ACTIVE: 'bg-success/10 text-success',
    PAUSED: 'bg-warning/10 text-warning',
    EXPIRED: 'bg-destructive/10 text-destructive',
    ARCHIVED: 'bg-muted text-muted-foreground line-through',
};

export const POLICY_LEVEL_LABEL: Record<PolicyLevel, string> = {
    BRONZE: 'Bronce',
    SILVER: 'Plata',
    GOLD: 'Oro',
    ALL: 'Todos los niveles',
};

/** Etiqueta y unidad de cada palanca, tal como las nombra el mockup. */
export const POLICY_LEVER_LABEL: Record<PolicyLeverKey, string> = {
    sciMin: 'SCI mínimo',
    downMinPct: 'Enganche mínimo',
    tna: 'TNA especial',
    maxAmount: 'Monto máx especial',
};

export const POLICY_LEVER_UNIT: Record<PolicyLeverKey, string> = {
    sciMin: 'pts',
    downMinPct: '%',
    tna: '%',
    maxAmount: '$',
};

/**
 * Las palancas que admiten AFLOJAR. `sciMin` no está: el contrato solo la expone como piso, así que
 * su dirección es siempre endurecer.
 */
export const EASABLE_LEVERS: readonly PolicyLeverKey[] = ['downMinPct', 'tna', 'maxAmount'];

export const POLICY_LOG_ACTION_LABEL: Record<StorePolicyLogAction, string> = {
    CREATE: 'Alta',
    UPDATE: 'Edición',
    STATUS_CHANGE: 'Cambio de estado',
};
